"use client"

import { useState } from "react"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  parseNum,
} from "./calculator-ui"

/**
 * Google Ads cost estimator: monthly budget + industry benchmarks (avg CPC and
 * conversion rate on the Search network) → estimated clicks, conversions, and
 * cost per lead. Selecting an industry prefills CPC/CVR; both stay editable so
 * advertisers can plug in their own account numbers.
 *
 * Benchmark values are directional US Search-network averages compiled from
 * published industry benchmark reports (LocaliQ/WordStream style datasets).
 */
const INDUSTRIES = [
  { label: "All industries (average)", cpc: 4.66, cvr: 7.5 },
  { label: "Arts & Entertainment", cpc: 1.72, cvr: 13.4 },
  { label: "Attorneys & Legal Services", cpc: 8.94, cvr: 7.5 },
  { label: "Automotive Repair & Services", cpc: 3.06, cvr: 12.6 },
  { label: "Beauty & Personal Care", cpc: 3.56, cvr: 8.2 },
  { label: "Business Services", cpc: 5.37, cvr: 5.1 },
  { label: "Dentists & Dental Services", cpc: 7.15, cvr: 13.8 },
  { label: "Education & Instruction", cpc: 4.17, cvr: 8.7 },
  { label: "Finance & Insurance", cpc: 5.16, cvr: 5.4 },
  { label: "Health & Fitness", cpc: 4.71, cvr: 9.8 },
  { label: "Home & Home Improvement", cpc: 6.96, cvr: 10.9 },
  { label: "Real Estate", cpc: 2.1, cvr: 3.3 },
  { label: "Restaurants & Food", cpc: 2.18, cvr: 6.9 },
  { label: "Shopping & Ecommerce", cpc: 2.72, cvr: 4.0 },
  { label: "Travel", cpc: 1.92, cvr: 5.4 },
] as const

export function GoogleAdsCostWidget() {
  const [industry, setIndustry] = useState<string>(INDUSTRIES[0].label)
  const [budget, setBudget] = useState("1500")
  const [cpc, setCpc] = useState(String(INDUSTRIES[0].cpc))
  const [cvr, setCvr] = useState(String(INDUSTRIES[0].cvr))

  function handleIndustryChange(label: string) {
    setIndustry(label)
    const row = INDUSTRIES.find((i) => i.label === label)
    if (row) {
      setCpc(String(row.cpc))
      setCvr(String(row.cvr))
    }
  }

  const budgetNum = parseNum(budget)
  const cpcNum = parseNum(cpc)
  const cvrNum = parseNum(cvr)

  const clicks =
    budgetNum !== null && cpcNum !== null && cpcNum > 0
      ? budgetNum / cpcNum
      : NaN
  const conversions =
    Number.isFinite(clicks) && cvrNum !== null ? clicks * (cvrNum / 100) : NaN
  const cpl =
    budgetNum !== null && Number.isFinite(conversions) && conversions > 0
      ? budgetNum / conversions
      : NaN

  return (
    <CalculatorCard title="Google Ads Cost Calculator">
      <CalculatorGrid>
        <div className="flex flex-col gap-2">
          <Label
            htmlFor="gac-industry"
            className="text-sm font-medium text-foreground"
          >
            Industry
          </Label>
          <Select value={industry} onValueChange={handleIndustryChange}>
            <SelectTrigger id="gac-industry" className="w-full">
              <SelectValue placeholder="Select an industry" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map((i) => (
                <SelectItem key={i.label} value={i.label}>
                  {i.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Prefills the benchmark CPC and conversion rate — edit either to
            match your account.
          </p>
        </div>
        <NumberField
          id="gac-budget"
          label="Monthly budget"
          value={budget}
          onChange={setBudget}
          prefix="$"
        />
        <NumberField
          id="gac-cpc"
          label="Average cost per click (CPC)"
          value={cpc}
          onChange={setCpc}
          prefix="$"
          hint="Search-network benchmark for the selected industry."
        />
        <NumberField
          id="gac-cvr"
          label="Conversion rate"
          value={cvr}
          onChange={setCvr}
          suffix="%"
          hint="Share of clicks that become a lead or sale."
        />
      </CalculatorGrid>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <ResultStat
          label="Est. clicks / month"
          value={
            Number.isFinite(clicks)
              ? Math.round(clicks).toLocaleString("en-US")
              : "—"
          }
        />
        <ResultStat
          label="Est. conversions / month"
          value={
            Number.isFinite(conversions)
              ? conversions.toLocaleString("en-US", {
                  maximumFractionDigits: 1,
                })
              : "—"
          }
        />
        <ResultStat label="Cost per lead" value={formatCurrency(cpl)} />
      </div>

      <FormulaNote>
        Clicks = Budget ÷ CPC · Conversions = Clicks × Conversion rate · Cost
        per lead = Budget ÷ Conversions. Benchmarks are directional US Search
        averages — your actual CPC depends on keywords, location, and Quality
        Score.
      </FormulaNote>
    </CalculatorCard>
  )
}
